import type { CreateAccountInput } from '@/data/api'
import type { Role } from '@/data/types'
import { displayToPaise, parseCurrencyType } from '@/lib/currency'
import { roleToLevel } from '@/api/map/agent'

/**
 * Hierarchy `POST /agents` body (OpenAPI CreateAgentRequest + local login fields).
 * Mock hierarchy provisions Keycloak when username+password are present.
 */
export interface CreateAgentRequest {
  parentId?: string
  name: string
  creditLimit: string
  shareBp: number
  shareCasinoBp?: number
  commissionBp: number
  commissionBookmakerBp?: number
  commissionMatchOddsBp?: number
  member: boolean
  /** Target hierarchy level (2–5 agent, 6 member). Skip-level creates allowed. */
  level?: number
  /** Console username — used for Keycloak login email locally. */
  username?: string
  /** Login password — provisioned into Keycloak by mock-hierarchy (dev). */
  password?: string
  /** Optional explicit login identity; defaults to username (may include `@`). */
  email?: string
  comments?: string
  games?: {
    casino?: boolean
    cardGame?: boolean
    liveGame?: boolean
    sportsbook?: boolean
  }
  betMin?: string
  betMax?: string
  currencyType?: string
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType?: string
  /** Lab fundistCurrencyType — Crash Games. */
  fundistCurrencyType?: string
  /**
   * Winner7 downline max credit — level → minor units.
   * e.g. { "5": "600000", "6": "500000" }.
   */
  downlineMaxCredit?: Record<string, string>
}

/** Major units (₹) → minor units (paise) as decimal string. */
export function toMinorUnits(major: number): string {
  if (!Number.isFinite(major) || major < 0) return '0'
  return String(Math.round(major * 100))
}

/** Share UI value → basis points (SportsBook).
 * INR Member Rate 0–1 → bp = rate × 10000.
 * POINTS My share any integer % → bp = points × 100 (uncapped).
 */
export function sharePointsToBp(
  points: number,
  currency: 'INR' | 'POINTS' | 'POINT@1' | string = 'POINTS',
): number {
  if (!Number.isFinite(points) || points < 0) return 0
  if (parseCurrencyType(currency) === 'INR') {
    return Math.min(10_000, Math.round(points * 10_000))
  }
  return Math.round(points * 100)
}

/**
 * Lab Live Casino Member Rate → basis points (always % × 100).
 * INR UI is 0–100; POINTS / POINT@1 accept any finite number.
 */
export function casinoSharePointsToBp(points: number): number {
  if (!Number.isFinite(points)) return 0
  return Math.round(points * 100)
}

/** Commission percent display value (e.g. 0.96 for 0.96%) → basis points. */
export function commissionPercentToBp(percent: number): number {
  if (!Number.isFinite(percent) || percent < 0) return 0
  return Math.min(10_000, Math.round(percent * 100))
}

/**
 * Username → Keycloak email field for provision.
 * Lab usernames with `@` stay as-is; bare names use `VITE_LOGIN_EMAIL_DOMAIN`
 * (no hardcoded product domain in source).
 */
export function loginEmailFromUsername(username: string): string {
  const raw = username.trim().toLowerCase()
  if (!raw) return ''
  if (raw.includes('@')) return raw
  const envDomain = import.meta.env.VITE_LOGIN_EMAIL_DOMAIN
  const domain =
    typeof envDomain === 'string' &&
    envDomain.trim() !== '' &&
    envDomain.trim().toLowerCase() !== 'undefined'
      ? envDomain.trim().toLowerCase().replace(/^@+/, '')
      : ''
  if (!domain) return raw
  return `${raw}@${domain}`
}

export function toCreateAgentRequest(
  input: CreateAccountInput,
  parentId: string,
): CreateAgentRequest {
  const username = input.username.trim().toLowerCase()
  const isMember = input.role === 'player'
  const currencyType = parseCurrencyType(input.currencyType)
  const downlineMaxCredit = toDownlineMaxCreditPaise(input.downlineMaxCredit, currencyType)
  return {
    parentId,
    name: input.displayName.trim() || username,
    creditLimit: displayToPaise(input.creditLimit, currencyType),
    currencyType,
    ...(input.casinoCurrencyType
      ? { casinoCurrencyType: input.casinoCurrencyType }
      : {}),
    ...(input.fundistCurrencyType
      ? { fundistCurrencyType: input.fundistCurrencyType }
      : {}),
    // Members → SportsBook: INR leaf 10000 bp; POINTS keeps Master's My Share.
    // Casino: INR leaf; POINTS entered.
    shareBp:
      isMember && currencyType === 'INR'
        ? 10_000
        : sharePointsToBp(input.share, currencyType),
    shareCasinoBp: isMember
      ? currencyType === 'INR'
        ? 10_000
        : casinoSharePointsToBp(input.shareCasino ?? input.share)
      : casinoSharePointsToBp(input.shareCasino ?? input.share),
    commissionBp: commissionPercentToBp(input.commission.fancy),
    commissionBookmakerBp: commissionPercentToBp(input.commission.bookmaker),
    commissionMatchOddsBp: commissionPercentToBp(input.commission.matchOdds),
    member: isMember,
    level: roleToLevel(input.role),
    username,
    password: input.password,
    email: loginEmailFromUsername(username),
    comments: input.comments?.trim() || undefined,
    games: input.games,
    ...(downlineMaxCredit ? { downlineMaxCredit } : {}),
    ...(isMember
      ? {
          betMin: displayToPaise(input.betLimits.min, currencyType),
          betMax: displayToPaise(input.betLimits.max, currencyType),
        }
      : {}),
  }
}

/** Role-keyed display amounts → level-keyed paise for POST /v1/agents. */
export function toDownlineMaxCreditPaise(
  downlineMaxCredit: Partial<Record<Role, number>> | undefined,
  currencyType: string,
): Record<string, string> | undefined {
  if (!downlineMaxCredit) return undefined
  const ccy = parseCurrencyType(currencyType)
  const out: Record<string, string> = {}
  for (const [role, amount] of Object.entries(downlineMaxCredit)) {
    if (amount == null || !Number.isFinite(amount) || amount < 1) continue
    out[String(roleToLevel(role as Role))] = displayToPaise(amount, ccy)
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Hierarchy `PATCH /v1/agents/{id}` body (OpenAPI UpdateAgentRequest + local password).
 */
export interface UpdateAgentRequest {
  name?: string
  creditLimit?: string
  shareBp?: number
  shareCasinoBp?: number
  commissionBp?: number
  /** Lab Member.bookmakerCommission — distinct from fancy commissionBp. */
  commissionBookmakerBp?: number
  status?: 'active' | 'inactive' | 'suspended' | 'closed'
  comments?: string
  /** Mock-hierarchy only — resets Keycloak password when present. */
  password?: string
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType?: string
  /** Lab fundistCurrencyType — Crash Games. */
  fundistCurrencyType?: string
}

export function toUpdateAgentRequest(input: {
  displayName: string
  creditLimit: number
  share: number
  shareCasino: number
  commission: { fancy: number; bookmaker: number; matchOdds: number }
  status: 'active' | 'inactive' | 'suspended' | 'closed'
  comments?: string
  password?: string
  currencyType?: string
  casinoCurrencyType?: 'HKD' | 'AED'
  fundistCurrencyType?: 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'
}): UpdateAgentRequest {
  const currencyType = parseCurrencyType(input.currencyType)
  const body: UpdateAgentRequest = {
    name: input.displayName.trim(),
    creditLimit: displayToPaise(input.creditLimit, currencyType),
    shareBp: sharePointsToBp(input.share, currencyType),
    shareCasinoBp: casinoSharePointsToBp(input.shareCasino),
    commissionBp: commissionPercentToBp(input.commission.fancy),
    commissionBookmakerBp: commissionPercentToBp(input.commission.bookmaker),
    status: input.status,
    // Always send comments (including "") so clear/edit persists on live.
    comments: input.comments?.trim() ?? '',
  }
  const password = input.password?.trim()
  if (password) body.password = password
  if (input.casinoCurrencyType) {
    body.casinoCurrencyType = input.casinoCurrencyType
  }
  if (input.fundistCurrencyType) {
    body.fundistCurrencyType = input.fundistCurrencyType
  }
  return body
}
